import { Badge, Box, Stack, Text, VStack } from '@chakra-ui/react';
import {
  BackofficeActivitiesOrderItemWithVoucher,
  VOUCHER_REWARD_TYPE,
} from '@diamond/shared/types';
import { PromoPrice } from '@diamond/shared/ui';
import { QueryKey } from '@tanstack/react-query';

import { ColumnComponentProps } from '../../../types/activity-column-types';
import {
  formatCurrency,
  FulfillmentStage,
  getActivityItemDisplay,
  getPromoAdjustedUnitPrice,
  getVoucherAdjustedUnitPrice,
  resolveFulfillmentStageStrikethroughBase,
  resolveFulfillmentStageUnitPrice,
} from '../../../utils/activity-formatters';
import { ActivityDetailDiffLabel } from '../../activity-detail/activity-detail-stage-qty-cell';
import { LatestPriceColumn } from './latest-price-column';

type Props = ColumnComponentProps<
  BackofficeActivitiesOrderItemWithVoucher,
  number | undefined
> & {
  stage: FulfillmentStage;
  isVoucherEnabled?: boolean;
  editable?: boolean;
  selectedData: Parameters<typeof LatestPriceColumn>[0]['selectedData'];
  onUpdate: Parameters<typeof LatestPriceColumn>[0]['onUpdate'];
  queryKey?: QueryKey;
  activitiesId: string;
  token?: string;
  refetch: () => void;
};

export function FulfillmentPriceColumn({
  editable = false,
  stage,
  ...props
}: Props) {
  const item = props.info.row.original;
  const isEditingPrice =
    editable &&
    item.can_overwrite_price &&
    props.selectedData.some((row) => row.original.id === item.id);

  if (isEditingPrice) {
    return (
      <LatestPriceColumn
        {...props}
        stage={stage}
        isVoucherEnabled={props.isVoucherEnabled}
      />
    );
  }

  const formatCurrencyNoDecimals = (value: number) =>
    `Rp${Math.trunc(value).toLocaleString('id-ID')}`;

  const displayValue = resolveFulfillmentStageUnitPrice(
    item,
    stage,
    props.isVoucherEnabled
  );
  const display = getActivityItemDisplay(item);
  const strikethroughBase = resolveFulfillmentStageStrikethroughBase(
    item,
    stage
  );
  const promoAdjustedPrice = getPromoAdjustedUnitPrice(item);
  const originalPrice = strikethroughBase;
  let discountAmount: number | undefined = item.deduction_amount;

  if (props.isVoucherEnabled && item.has_voucher_discount) {
    const voucherResult = getVoucherAdjustedUnitPrice(
      promoAdjustedPrice,
      item,
      props.isVoucherEnabled
    );
    discountAmount = voucherResult.discountAmount ?? discountAmount;
  }

  const hasNominalVoucher =
    props.isVoucherEnabled &&
    item.voucher_reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT;

  const isPromoNominalDiscount =
    item.is_discount &&
    !item.has_voucher_discount &&
    (discountAmount === undefined || discountAmount === null) &&
    item.price_after_discount !== undefined &&
    item.price_after_discount !== originalPrice;

  const promoNominalDiscountAmount = isPromoNominalDiscount
    ? originalPrice - (item.price_after_discount ?? originalPrice)
    : 0;

  if (!display.showPriceSection) {
    return null;
  }

  return (
    <VStack align="center" spacing={0.5} w="full">
      <Stack align="center" spacing={0.5} w="full">
        <Text
          fontSize="sm"
          fontWeight="400"
          color="#373F41"
          textAlign="center"
          w="full"
        >
          {formatCurrency(displayValue || 0)}
        </Text>
        {item.is_discount || item.has_voucher_discount ? (
          hasNominalVoucher || isPromoNominalDiscount ? (
            <Box
              display="flex"
              gap="1"
              alignItems="center"
              justifyContent="center"
              flexWrap="wrap"
            >
              <Text
                color="gray.75"
                textDecoration="line-through"
                fontSize="sm"
                fontWeight="400"
              >
                {formatCurrency(originalPrice)}
              </Text>
              <Badge
                color="#FF0000"
                bgColor="#FFDADA"
                rounded="4px"
                fontSize="sm"
                fontWeight="400"
              >
                -
                {formatCurrencyNoDecimals(
                  hasNominalVoucher && item.voucher_discount_amount
                    ? item.voucher_discount_amount
                    : promoNominalDiscountAmount
                )}
              </Badge>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" w="full">
              <PromoPrice
                formattedPrice={formatCurrency(originalPrice)}
                discountAmount={discountAmount}
                isActive
                isFreeGoods={false}
              />
            </Box>
          )
        ) : null}
      </Stack>
      {display.showInitialUnitPrice ? (
        <ActivityDetailDiffLabel
          label="harga awal"
          value={formatCurrency(display.initialUnitPrice)}
          align="center"
        />
      ) : null}
    </VStack>
  );
}
